import axios from 'axios';
import type { AutocompleteModel as PropertyModel } from '../../../properties/_runtime/services/PropertiesService';

export default class CategoriesService
{
    public static async getFolders()
    {
        return (await axios.get<Pagination<Resource<FolderModel>>>(`admin/store/categories/folders`)).data;
    }

    public static async fetchFolder(id: number)
    {
        return (await axios.get<Resource<FolderModel>>(`admin/store/categories/folders/${id}`)).data;
    }

    public static async createFolder(model: FolderModel)
    {
        return (await axios.post<ResponseModel>(`admin/store/categories/folders`, model)).data;
    }

    public static async updateFolder(id: number, model: FolderModel)
    {
        return (await axios.put<Statement>(`admin/store/categories/folders/${id}`, model)).data;
    }

    public static async removeFolder(id: number)
    {
        return (await axios.delete(`admin/store/categories/folders/${id}`)).data as Statement;
    }

    // ----------------------------------------------------------------------------------------------------

    public static async getCategories(folders: number[], expanded: number[])
    {
        return (await axios.get<Pagination<Resource<CategoryListItemModel>>>('admin/store/categories', {
            params: {
                folders: folders.join(','),
                expanded: expanded.join(',')
            }
        })).data;
    }

    public static async getCategoriesByFolder(folderId: number)
    {
        return (await axios.get<CategoryListItemModel[]>('admin/store/categories/options', {
            params: {
                folder: folderId
            }
        })).data;
    }

    public static async getCategoriesForShop(shopId: number)
    {
        return (await axios.get<CategoryListItemModel[]>(`admin/store/categories/shop/${shopId}`)).data;
    }

    public static async fetchCategory(id: number)
    {
        return (await axios.get<Resource<CategoryFormModel>>(`admin/store/categories/${id}`)).data;
    }

    public static async createCategory(model: CategoryFormModel)
    {
        return (await axios.post<ResponseModel>(`admin/store/categories`, model)).data;
    }

    public static async updateCategory(id: number, model: CategoryFormModel)
    {
        return (await axios.put<Statement>(`admin/store/categories/${id}`, model)).data;
    }

    public static async removeCategory(id: number)
    {
        return (await axios.delete<Statement>(`admin/store/categories/${id}`)).data;
    }

    public static async sortCategories(positions: Record<number, number>)
    {
        return (await axios.put<Statement>(`admin/store/categories/positions`, positions)).data;
    }

    public static async getAutocompleteList(search: string, limit: number = 10, excluded: number[] = null)
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/categories/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                excluded: excluded
            }
        })).data;
    }

    public static async fetchAutocompleteModel(id: number)
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/categories/autocomplete/${id}`)).data;
    }

    public static async getPropertiesList(propertiesIds: number[], parentId: number)
    {
        return (await axios.get<PropertiesModel>(`admin/store/categories/properties`, {
            params: {
                propertiesIds: propertiesIds,
                parentId: parentId
            }
        })).data;
    }

    public static async getCategoriesForTreeSelect()
    {
        return (await axios.get<CategoryListItemModel[]>(`admin/store/categories/tree-select`)).data;
    }

    public static async getCategoriesByIds(categoriesIds: number[], productId: number = null)
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/categories/ids`, {
            params: {
                categoriesIds: categoriesIds,
                productId: productId
            }
        })).data;
    }
}

export interface FolderModel
{
    id: number;
    name: string;
}

export interface CategoryListItemModel
{
    id: number;
    parentId?: number;
    folderId: number;
    name: string;
    option: string;
    level: number;
    hasChildren: boolean;
    position: number;
}

export interface CategoryFormModel
{
    id: number;
    folderId: number;
    parentId?: number;
    name: string;
    displayName: Record<string, string>;
    symbol: Record<string, string>;
    isActive: boolean;
    picture?: File;
    thumb?: File;
    description: Record<string, string>;
    keywords: Record<string, string>;

    quantityMargin: number;
    quantityMarginUnit: string;
    propertiesIds: number[];

    meta: MetaSettingsModel | null;
}

export interface PropertiesModel
{
    properties?: PropertyModel[];
    inheritedProperties?: Record<string, PropertyModel[]>;
}

export interface CategoryPosition
{
    id: number;
    position: number;
}

export interface ResponseModel
{
    id: number;
}

export interface AutocompleteListModel
{
    id: number;
    name: string;
    isActive: boolean;
    isMain: boolean;
}
